// Single canonical vocabulary for food allergens + dietary tags.
//
// Every surface that reads or writes `menu_items.allergens`,
// `menu_items.dietary_tags`, or customer allergen-avoid lists must resolve
// stored values through this module, so 'milk' / 'dairy' / 'Milk' collapse to
// one canonical key instead of three unmatched free-text strings (doc 29 / doc
// 40). The merchant editor stores the authoritative `label` values; customer
// profiles and filters may use colloquial `aliases`; matching always goes
// through `resolveAllergenKey`/`resolveDietaryTagKey`.
//
// Pure module: no `'use client'`, no I/O, importable from server and client.

export interface AllergenEntry {
  /** Stable machine key (lowercase snake_case). Also the filter token. */
  key: string;
  /** Authoritative merchant label, the value stored on `menu_items.allergens`. */
  label: string;
  /** Colloquial tokens accepted when matching (profiles, legacy data). */
  aliases: string[];
  /** Editor/filter group. */
  category: 'cereals' | 'fish' | 'nuts' | 'dairy' | 'other';
}

export const ALLERGEN_ENTRIES: readonly AllergenEntry[] = [
  { key: 'celery', label: 'Celery', aliases: ['celery'], category: 'other' },
  {
    key: 'gluten',
    label: 'Cereals containing gluten',
    aliases: ['gluten', 'wheat', 'cereals', 'cereals containing gluten', 'oats', 'barley', 'rye'],
    category: 'cereals',
  },
  {
    key: 'crustaceans',
    label: 'Crustaceans',
    aliases: ['crustaceans', 'shellfish', 'prawns', 'prawn', 'shrimp', 'crab', 'lobster'],
    category: 'fish',
  },
  { key: 'eggs', label: 'Eggs', aliases: ['eggs', 'egg'], category: 'other' },
  { key: 'fish', label: 'Fish', aliases: ['fish'], category: 'fish' },
  { key: 'lupin', label: 'Lupin', aliases: ['lupin', 'lupins'], category: 'other' },
  { key: 'milk', label: 'Milk', aliases: ['milk', 'dairy', 'lactose'], category: 'dairy' },
  {
    key: 'molluscs',
    label: 'Molluscs',
    aliases: ['molluscs', 'mollusks', 'mollusk', 'mussels', 'oysters', 'clams'],
    category: 'fish',
  },
  { key: 'mustard', label: 'Mustard', aliases: ['mustard'], category: 'other' },
  { key: 'peanuts', label: 'Peanuts', aliases: ['peanuts', 'peanut'], category: 'nuts' },
  { key: 'sesame', label: 'Sesame', aliases: ['sesame'], category: 'other' },
  { key: 'soy', label: 'Soybeans', aliases: ['soy', 'soybeans', 'soya', 'soybean'], category: 'other' },
  {
    key: 'sulphites',
    label: 'Sulphur dioxide and sulphites',
    aliases: ['sulphur dioxide and sulphites', 'sulphur dioxide', 'sulphites', 'sulfites'],
    category: 'other',
  },
  {
    key: 'tree_nuts',
    label: 'Tree nuts',
    aliases: ['tree nuts', 'tree nut', 'nuts', 'nut', 'almond', 'walnut', 'cashew', 'pecan', 'hazelnut'],
    category: 'nuts',
  },
];

/** Canonical keys, in the same order as the merchant editor list. */
export const ALLERGEN_KEYS: readonly string[] = ALLERGEN_ENTRIES.map((e) => e.key);

/** Authoritative merchant labels, in the same order as the merchant editor list. */
export const ALLERGEN_LABELS: readonly string[] = ALLERGEN_ENTRIES.map((e) => e.label);

const ALIAS_TO_KEY = new Map<string, string>();
for (const entry of ALLERGEN_ENTRIES) {
  ALIAS_TO_KEY.set(entry.key, entry.key);
  ALIAS_TO_KEY.set(entry.label.toLowerCase(), entry.key);
  for (const alias of entry.aliases) ALIAS_TO_KEY.set(alias.toLowerCase(), entry.key);
}

function normalizeToken(value: string): string {
  return value.trim().toLowerCase();
}

/** Resolve a stored/entered allergen value to its canonical key, or null. */
export function resolveAllergenKey(value: string): string | null {
  return ALIAS_TO_KEY.get(normalizeToken(value)) ?? null;
}

/** True when any stored value resolves to one of the wanted canonical keys. */
export function matchesAnyAllergen(
  stored: readonly string[] | null | undefined,
  wantedKeys: readonly string[],
): boolean {
  if (!stored || stored.length === 0 || wantedKeys.length === 0) return false;
  const want = new Set(wantedKeys);
  for (const value of stored) {
    const key = resolveAllergenKey(String(value));
    if (key && want.has(key)) return true;
  }
  return false;
}

/**
 * Dedupe stored allergen values into canonical labels, preserving order and
 * dropping unrecognised tokens. Use for display (KDS/POS tickets) so a menu
 * item that stores both "Milk" and "dairy" renders one "Milk" chip.
 */
export function canonicalAllergenLabels(stored: readonly string[] | null | undefined): string[] {
  if (!stored || stored.length === 0) return [];
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const value of stored) {
    const key = resolveAllergenKey(String(value));
    const entry = key ? ALLERGEN_ENTRIES.find((e) => e.key === key) : undefined;
    const label = entry?.label ?? String(value);
    if (!seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels;
}

// ---- Dietary tags (positive claims, never auto-derived — doc 29 threat model) ----

export interface DietaryTagEntry {
  /** Stable machine key (lowercase snake_case). Also the filter token. */
  key: string;
  /** The label stored on `menu_items.dietary_tags`. */
  label: string;
}

export const DIETARY_TAG_ENTRIES: readonly DietaryTagEntry[] = [
  { key: 'vegan', label: 'Vegan' },
  { key: 'vegetarian', label: 'Vegetarian' },
  { key: 'halal', label: 'Halal' },
  { key: 'kosher', label: 'Kosher' },
  { key: 'gluten_free', label: 'Gluten-free' },
  { key: 'dairy_free', label: 'Dairy-free' },
  { key: 'nut_free', label: 'Nut-free' },
];

export const DIETARY_TAG_KEYS: readonly string[] = DIETARY_TAG_ENTRIES.map((e) => e.key);

const DIETARY_TAG_KEY_BY_TOKEN = new Map<string, string>();
for (const entry of DIETARY_TAG_ENTRIES) {
  DIETARY_TAG_KEY_BY_TOKEN.set(entry.key, entry.key);
  DIETARY_TAG_KEY_BY_TOKEN.set(entry.label.toLowerCase(), entry.key);
  DIETARY_TAG_KEY_BY_TOKEN.set(entry.label.replace('-', '_').toLowerCase(), entry.key);
}

/** Resolve a stored dietary-tag value to its canonical key, or null. */
export function resolveDietaryTagKey(value: string): string | null {
  return DIETARY_TAG_KEY_BY_TOKEN.get(normalizeToken(value)) ?? null;
}

/** True when any stored tag resolves to one of the wanted canonical keys. */
export function matchesAnyDietaryTag(
  stored: readonly string[] | null | undefined,
  wantedKeys: readonly string[],
): boolean {
  if (!stored || stored.length === 0 || wantedKeys.length === 0) return false;
  const want = new Set(wantedKeys);
  for (const value of stored) {
    const key = resolveDietaryTagKey(String(value));
    if (key && want.has(key)) return true;
  }
  return false;
}
